import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../../../contexts/AuthContext";
import {
  getCompleteLawyerProfile,
  getLawyerReviews,
  getLawyerStats,
  createReview,
} from "@/shared/db";
import {
  ReviewsList,
  ReviewForm,
  RatingDisplay,
  RatingBreakdown,
  Button,
} from "@/shared/ui";
import {
  CheckBadgeIcon,
  BriefcaseIcon,
  AcademicCapIcon,
  CurrencyDollarIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ConsultationRequestModal } from "../components/ConsultationRequestModal";

interface Lawyer {
  id: string;
  university: string;
  years_of_experience: number;
  practice_areas: string[];
  accept_clients: boolean;
  accept_cases: boolean;
  hourly_rate?: number;
  is_verified: boolean;
  plan_type?: string | null;
  bar_number?: string;
  phone?: string;
  bio?: string;
  profiles: {
    full_name: string;
    profile_image_url?: string;
    country: string;
    email?: string;
  };
}

export const PublicLawyerProfilePage: React.FC = () => {
  const { lawyerId } = useParams<{ lawyerId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [lawyerData, setLawyerData] = useState<Lawyer | null>(null);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [showConsultationModal, setShowConsultationModal] = useState(false);

  useEffect(() => {
    if (lawyerId) {
      loadLawyerProfile();
      loadReviews();
      loadStats();
    }
  }, [lawyerId]);

  const loadLawyerProfile = async () => {
    if (!lawyerId) return;

    try {
      setLoading(true);
      const data = await getCompleteLawyerProfile(lawyerId);
      setLawyerData(data as unknown as Lawyer);
    } catch (error) {
      toast.error(t("public.lawyerProfile.errors.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const loadReviews = async () => {
    if (!lawyerId) return;

    try {
      const reviewsData = await getLawyerReviews(lawyerId, {
        limit: 10,
        sortBy: "recent",
      });
      setReviews(reviewsData);
    } catch (error) {
      toast.error(t("public.lawyerProfile.errors.loadError"));
    }
  };

  const loadStats = async () => {
    if (!lawyerId) return;

    try {
      const statsData = await getLawyerStats(lawyerId);
      setStats(statsData);
    } catch (error) {
      toast.error(t("public.lawyerProfile.errors.loadError"));
    }
  };

  const handleSubmitReview = async (data: {
    rating: number;
    comment: string;
  }) => {
    if (!lawyerId || !user) return;

    try {
      setSubmittingReview(true);
      await createReview({
        lawyerId,
        clientId: user.id,
        rating: data.rating,
        comment: data.comment,
      });
      toast.success(t("public.lawyerProfile.review.success"));
      setShowReviewForm(false);
      // Reload reviews and stats
      loadReviews();
      loadStats();
      loadLawyerProfile();
    } catch (error: any) {
      toast.error(error.message || t("public.lawyerProfile.review.error"));
    } finally {
      setSubmittingReview(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getPracticeAreaName = (code: string) => {
    return t(`lawyer.practiceAreas.${code}`, code);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-tiber-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-tiber-700">{t("public.lawyerProfile.loading")}</p>
        </div>
      </div>
    );
  }

  if (!lawyerData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-tiber-700 mb-4">
            {t("public.lawyerProfile.notFound")}
          </p>
          <button
            onClick={() => navigate("/lawyers")}
            className="text-tiber-600 hover:text-tiber-700"
          >
            {t("public.lawyerProfile.backToDirectory")}
          </button>
        </div>
      </div>
    );
  }

  const profile = lawyerData.profiles;

  return (
    <div className="min-h-screen bg-romance-50">
      {/* Enhanced Header Navigation */}
      <div className="bg-tiber-600 shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button
            onClick={() => navigate("/lawyers")}
            className="flex items-center text-white hover:text-tiber-100 cursor-pointer transition-colors font-medium"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            {t("public.lawyerProfile.backToDirectory")}
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Enhanced Profile Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-xl p-10 mb-8 border-t-4 border-tiber-600"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
            {/* Enhanced Avatar */}
            <div className="relative">
              {profile?.profile_image_url ? (
                <img
                  src={profile.profile_image_url}
                  alt={profile.full_name}
                  className="h-40 w-40 rounded-full object-cover border-4 border-tiber-600 shadow-lg"
                />
              ) : (
                <div className="h-40 w-40 rounded-full bg-tiber-100 flex items-center justify-center border-4 border-tiber-300 shadow-lg">
                  <span className="text-tiber-600 font-bold text-4xl">
                    {profile && getInitials(profile.full_name)}
                  </span>
                </div>
              )}
              {lawyerData.is_verified && (
                <div className="absolute -top-2 -right-2 bg-atlantis-500 rounded-full p-2 shadow-lg">
                  <CheckBadgeIcon
                    className="h-8 w-8 text-white"
                    title={t("public.lawyerProfile.verified")}
                  />
                </div>
              )}
            </div>

            {/* Enhanced Info */}
            <div className="flex-1">
              <div className="flex flex-col gap-3">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-4xl font-bold text-tiber-900">
                      {profile?.full_name}
                    </h1>
                    {lawyerData.plan_type === "lawyer_professional_pro" && (
                      <span
                        className="px-3 py-1 text-sm font-bold bg-tiber-500 text-white rounded-lg"
                        title={t("public.lawyerCard.proBadge", "Pro")}
                      >
                        Pro
                      </span>
                    )}
                  </div>
                  <p className="text-xl text-tiber-700 font-semibold">
                    {t("public.lawyerProfile.lawyer")} •{" "}
                    {lawyerData.years_of_experience}{" "}
                    {t("public.lawyerProfile.yearsExperience")}
                  </p>
                  {stats && (
                    <div className="mt-3">
                      <RatingDisplay
                        rating={stats.rating_average || 0}
                        totalReviews={stats.total_reviews || 0}
                        size="lg"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Enhanced Contact Info - respect show_email, show_phone privacy */}
              <div className="flex flex-wrap gap-4 text-sm text-tiber-700 mt-6 bg-tiber-50 rounded-lg p-4">
                {profile?.country && (
                  <div className="flex items-center gap-2">
                    <MapPinIcon className="h-5 w-5 text-tiber-600" />
                    <span className="font-medium">{profile.country}</span>
                  </div>
                )}
                {((lawyerData as { show_phone?: boolean }).show_phone === true || (lawyerData as { show_phone?: boolean }).show_phone == null) && lawyerData.phone && (
                  <div className="flex items-center gap-2">
                    <PhoneIcon className="h-5 w-5 text-tiber-600" />
                    <span className="font-medium">{lawyerData.phone}</span>
                  </div>
                )}
                {((lawyerData as { show_email?: boolean }).show_email === true || (lawyerData as { show_email?: boolean }).show_email == null) && profile?.email && (
                  <div className="flex items-center gap-2">
                    <EnvelopeIcon className="h-5 w-5 text-tiber-600" />
                    <span className="font-medium">{profile.email}</span>
                  </div>
                )}
              </div>

              {/* Enhanced CTA Button */}
              {user && user.id !== lawyerId && lawyerId && (
                <>
                  <button
                    onClick={() => setShowConsultationModal(true)}
                    className="mt-6 px-8 py-4 bg-tiber-600 text-white rounded-lg hover:bg-tiber-700 transition-all font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    {t("public.lawyerProfile.requestConsultation")}
                  </button>
                  <ConsultationRequestModal
                    isOpen={showConsultationModal}
                    onClose={() => setShowConsultationModal(false)}
                    lawyerId={lawyerId}
                    lawyerName={lawyerData.profiles?.full_name ?? ""}
                    practiceAreas={lawyerData.practice_areas ?? []}
                  />
                </>
              )}
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Enhanced About Section */}
            {lawyerData.bio && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-lg shadow-lg p-8 border-l-4 border-tiber-600"
              >
                <h2 className="text-2xl font-bold text-tiber-900 mb-4 flex items-center">
                  <BriefcaseIcon className="h-6 w-6 mr-2 text-tiber-600" />
                  {t("public.lawyerProfile.about")}
                </h2>
                <p className="text-tiber-800 leading-relaxed whitespace-pre-line text-lg">
                  {lawyerData.bio}
                </p>
              </motion.div>
            )}

            {/* Enhanced Practice Areas */}
            {lawyerData.practice_areas &&
              lawyerData.practice_areas.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-lg shadow-lg p-8 border-l-4 border-tiber-600"
                >
                  <h2 className="text-2xl font-bold text-tiber-900 mb-6 flex items-center">
                    <AcademicCapIcon className="h-6 w-6 mr-2 text-tiber-600" />
                    {t("public.lawyerProfile.practiceAreas")}
                  </h2>
                  <div className="flex flex-wrap gap-3">
                    {lawyerData.practice_areas.map((area: string) => (
                      <span
                        key={area}
                        className="px-6 py-3 bg-tiber-100 text-tiber-800 rounded-lg text-base font-semibold hover:bg-tiber-200 transition-colors border-2 border-tiber-300"
                      >
                        {getPracticeAreaName(area)}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}

            {/* Reviews Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-lg shadow p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-juridico">
                  {t("public.lawyerProfile.reviews.title")} (
                  {stats?.total_reviews || 0})
                </h2>
                {user && user.id !== lawyerId && !showReviewForm && (
                  <Button
                    onClick={() => setShowReviewForm(true)}
                    variant="primary"
                    size="sm"
                  >
                    {t("public.lawyerProfile.reviews.write")}
                  </Button>
                )}
              </div>

              {/* Rating Breakdown */}
              {stats && stats.total_reviews > 0 && (
                <div className="mb-6 pb-6 border-b">
                  <RatingBreakdown
                    breakdown={stats.rating_breakdown || []}
                    totalReviews={stats.total_reviews || 0}
                  />
                </div>
              )}

              {/* Review Form */}
              {showReviewForm && user && (
                <div className="mb-6 pb-6 border-b">
                  <ReviewForm
                    lawyerId={lawyerId || ""}
                    onSubmit={handleSubmitReview}
                    onCancel={() => setShowReviewForm(false)}
                    isSubmitting={submittingReview}
                  />
                </div>
              )}

              {/* Reviews List */}
              <ReviewsList
                reviews={reviews}
                currentUserId={user?.id}
                showActions={false}
              />
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Professional Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-lg shadow p-6"
            >
              <h3 className="text-lg font-bold text-juridico mb-4">
                {t("public.lawyerProfile.professionalInfo")}
              </h3>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center space-x-2 text-tiber-700 mb-1">
                    <AcademicCapIcon className="h-5 w-5" />
                    <span className="text-sm font-medium">
                      {t("public.lawyerProfile.university")}
                    </span>
                  </div>
                  <p className="text-juridico ml-7">{lawyerData.university}</p>
                </div>

                <div>
                  <div className="flex items-center space-x-2 text-tiber-700 mb-1">
                    <BriefcaseIcon className="h-5 w-5" />
                    <span className="text-sm font-medium">
                      {t("public.lawyerProfile.experience")}
                    </span>
                  </div>
                  <p className="text-juridico ml-7">
                    {lawyerData.years_of_experience}{" "}
                    {t("public.lawyerProfile.years")}
                  </p>
                </div>

                {lawyerData.bar_number && (
                  <div>
                    <div className="flex items-center space-x-2 text-tiber-700 mb-1">
                      <CheckBadgeIcon className="h-5 w-5" />
                      <span className="text-sm font-medium">
                        {t("public.lawyerProfile.barNumber")}
                      </span>
                    </div>
                    <p className="text-juridico ml-7 font-mono text-sm">
                      {lawyerData.bar_number}
                    </p>
                  </div>
                )}

                {lawyerData.hourly_rate && (
                  <div>
                    <div className="flex items-center space-x-2 text-tiber-700 mb-1">
                      <CurrencyDollarIcon className="h-5 w-5" />
                      <span className="text-sm font-medium">
                        {t("public.lawyerProfile.rate")}
                      </span>
                    </div>
                    <p className="text-juridico ml-7">
                      ${lawyerData.hourly_rate}/{t("public.lawyerProfile.hour")}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Availability */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-lg shadow p-6"
            >
              <h3 className="text-lg font-bold text-juridico mb-4">
                {t("public.lawyerProfile.availability")}
              </h3>
              <div className="space-y-2">
                {lawyerData.accept_cases && (
                  <div className="flex items-center space-x-2 text-atlantis-600">
                    <CheckBadgeIcon className="h-5 w-5" />
                    <span className="text-sm">
                      {t("public.lawyerProfile.acceptsCases")}
                    </span>
                  </div>
                )}
                {lawyerData.accept_clients && (
                  <div className="flex items-center space-x-2 text-atlantis-600">
                    <CheckBadgeIcon className="h-5 w-5" />
                    <span className="text-sm">
                      {t("public.lawyerProfile.acceptsClients")}
                    </span>
                  </div>
                )}
                {!lawyerData.accept_cases && !lawyerData.accept_clients && (
                  <p className="text-sm text-titan">
                    {t("public.lawyerProfile.notAccepting")}
                  </p>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};
